import { eyebrowField, surfaceField } from "./_shared";
/**
 * Banner section — a full-width promotional strip with optional headline,
 * subhead, CTA, and background image. Common use cases: seasonal sale
 * announcements, free-shipping promos, new collection launches.
 *
 * Unlocked: insertable via the editor's "+ Add section" picker. Multiple
 * banners per page are allowed (up to `maxPerTemplate`).
 */
export const bannerSchema = {
    type: "banner",
    name: { ar: "بانر", en: "Banner" },
    description: {
        ar: "شريط ترويجي بعنوان، مع زر وصورة اختياريين.",
        en: "A promotional strip with optional CTA and background image.",
    },
    maxPerTemplate: 3,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "شكل شريط الترويج.",
                en: "How the promotional strip is laid out.",
            },
            options: [
                {
                    value: "banner-classic",
                    label: { ar: "بطاقة", en: "Card" },
                },
                // Ported from the EasyOrders `bottom-banner` (sharp family,
                // 2026-07-20). The SAME layout the hero already ships as `billboard`
                // — a full-bleed photograph with the copy and CTA at opposite ends of
                // one row — reused here because sharp puts THREE of them on one page
                // and `hero.maxPerTemplate` is 1. The mid-page instances are banners
                // by every other measure (promotional strip, image + headline + CTA,
                // no page-leading role), and banner's content model already matches
                // field for field, so this is a variant rather than a raised hero cap
                // or a third section type.
                {
                    value: "banner-billboard",
                    label: { ar: "بيلبورد", en: "Billboard" },
                },
                // Ported from the EasyOrders `rnw-hero rnw-hero--left` (volta family,
                // 2026-07-20). The SAME engine the hero ships as `runway`, reused here
                // for exactly the reason `banner-billboard` was: volta puts a runway
                // block MID-PAGE, below its product rows, while its page-leading hero
                // is a different engine (`edition-hero-slider`) and
                // `hero.maxPerTemplate` is 1.
                //
                // Content model matches field for field — background photo, kicker,
                // headline, lede, one CTA — so this is a variant, not a raised hero cap
                // and not a third section type.
                //
                // MEASURED on volta-bags: `--rnw-h-dt: 57svh` = 513px from 768 up,
                // `--rnw-h-mb: 40svh` = 360px below, against the hero's flat 72vh.
                {
                    value: "banner-runway",
                    label: { ar: "رنواي", en: "Runway" },
                },
                // Ported from the EasyOrders `custom-static-promo` (hype family,
                // 2026-07-20). A CONTAINED rounded panel — 16px radius, artwork as a
                // cover background rather than a full-bleed band — with the copy
                // centred and a PILL call-to-action beneath it.
                //
                // A variant and not a fourth section type: the content model is the
                // banner's own, field for field (image, headline, subhead, one CTA,
                // alignment). It is not `banner-classic` either — measured on
                // hype-books, theirs is 332px at 1440 / 296 at 768 / 280 at 390, and
                // the height is CONTENT-DRIVEN through percentage padding
                // (`8%` top / `5%` bottom at ≥1024, `30%` top at mobile), where
                // `banner-classic` is a fixed `clamp(360px, 40vw, 480px)` box with an
                // 8px radius. Reusing classic would have been ~45% too tall at 1440
                // and the wrong shape at every width.
                {
                    value: "banner-promo",
                    label: { ar: "بانر ترويجي", en: "Promo panel" },
                },
                // Ported from the EasyOrders `edition-hero-slider` (peral family,
                // 2026-07-20). The SAME engine the hero ships as `edition`, reused here
                // for exactly the reason `banner-runway` and `banner-billboard` were:
                // peral puts a SECOND `edition-hero-slider` MID-PAGE (below its product
                // rows) while its page-leading hero is the `ab-slider` — same engine as
                // `edition`, text baked into the artwork — and `hero.maxPerTemplate`
                // is 1.
                //
                // The content model matches field for field — background photo,
                // headline, subhead, one CTA, alignment — so this is a variant, not a
                // raised hero cap and not a fifth section type. It renders as a single
                // static edition frame (the banner passes no `slide` blocks), which
                // reproduces the centred title/desc/gold-CTA + progress/nav chrome of
                // peral's mid-page instance; the multi-slide carousel is the one
                // departure, recorded in the peral subsection of the plan doc.
                //
                // MEASURED on peral-gallery: 1440x648 (72vh at a 900px viewport),
                // gold CTA `#c9a962`, centred, NO scrim on the artwork.
                {
                    value: "banner-edition",
                    label: { ar: "إديشن", en: "Edition" },
                },
            ],
            default: "banner-classic",
        },
        // The section-background slot (behind the promo box). Banner keeps its own
        // `backgroundColor` for the INNER box, so it takes just `surfaceField()` — not
        // the full `colorFields()`, which would collide on `backgroundColor`.
        surfaceField(),
        ...eyebrowField(),
        {
            id: "headline",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Headline" },
            validate: { max: 80 },
        },
        {
            id: "subhead",
            type: "textarea",
            i18n: true,
            label: { ar: "النص الفرعي", en: "Subhead" },
            validate: { max: 200 },
        },
        {
            id: "ctaLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص الزر", en: "CTA label" },
            validate: { max: 24 },
        },
        {
            id: "ctaHref",
            type: "select",
            label: { ar: "وجهة الزر", en: "CTA destination" },
            options: [
                {
                    value: "all_products",
                    label: { ar: "كل المنتجات", en: "All products" },
                },
                { value: "collections", label: { ar: "الفئات", en: "Collections" } },
                { value: "none", label: { ar: "بدون زر", en: "No button" } },
            ],
            default: "all_products",
        },
        {
            id: "image",
            type: "image",
            label: { ar: "صورة الخلفية", en: "Background image" },
            helpText: {
                ar: "حجم مقترح 1600×400 بكسل.",
                en: "Recommended: 1600×400 px.",
            },
            focalPoint: true,
        },
        {
            id: "backgroundColor",
            type: "color",
            label: { ar: "لون الخلفية", en: "Background color" },
            helpText: {
                ar: "بيظهر لما ميكونش في صورة.",
                en: "Used when no image is set.",
            },
            default: "#1D9E75",
            // `banner-billboard` has no flat-colour fallback — it is a photograph
            // edge to edge, and with no image it falls back to its own near-black
            // frame. Leaving this control live under that variant would be a dead
            // control, the defect class this workstream keeps finding.
            showIf: { variant: ["banner-classic"] },
        },
        {
            id: "overlayOpacity",
            type: "range",
            // The overlay only darkens a BACKGROUND IMAGE — with no image it has zero
            // visible effect. Gate the control on an image being set so it never looks
            // broken (it would otherwise show a slider that does nothing).
            // `banner-billboard` paints NO scrim at all (the source expects the
            // artwork to carry its own contrast), so it is gated out there too.
            showIf: { image: "$set", variant: ["banner-classic"] },
            label: { ar: "تعتيم الطبقة", en: "Overlay opacity" },
            helpText: {
                ar: "بيغمّق صورة الخلفية عشان النص يبان.",
                en: "Darkens the background image so the text stays readable.",
            },
            min: 0,
            max: 100,
            step: 5,
            default: 30,
        },
        {
            id: "textAlign",
            type: "select",
            label: { ar: "محاذاة النص", en: "Text alignment" },
            options: [
                { value: "start", label: { ar: "البداية", en: "Start" } },
                { value: "center", label: { ar: "الوسط", en: "Center" } },
            ],
            default: "center",
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
    ],
};
//# sourceMappingURL=banner.js.map